import React, { useState, useEffect, Fragment } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DIARY_COLOURS } from '../lib/diaryColours';

const DIARY_COLOUR_KEYS = Object.keys(DIARY_COLOURS);
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ProjectClientPoCard } from '../components/client-pos/ProjectClientPoCard';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

// Import extracted components
import { NotesSection, NOTE_CATEGORIES, NOTE_CATEGORY_COLORS, VariationsSection, VARIATION_STATUS_COLORS, ProjectHeader, ProjectTabs, OverviewTab, LiveCostCards, useProjectLiveCosts } from '../components/project-detail';

import { 
  ArrowLeft, 
  Plus, 
  MapPin, 
  Calendar, 
  User,
  ClipboardList,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  FileText,
  TrendingUp,
  TrendingDown,
  Receipt,
  Trash2,
  Edit,
  Image,
  X,
  Eye,
  EyeOff,
  HardHat,
  Users,
  FolderOpen,
  Send,
  Printer,
  Film,
  Play,
  CheckCircle2,
  RefreshCw,
  Link2,
  BookOpen
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { FileBrowser } from '../components/FileBrowser';
import { CostBreakdownSection } from '../components/CostBreakdownSection';
import { ProjectProgrammeSection } from '../components/ProjectProgrammeSection';
import { CaptureFAB } from '../components/capture/CaptureFAB';

// Project type options (same as tenders)
const PROJECT_TYPE_OPTIONS = [
  'Extension', 'Loft Conversion', 'Garage Conversion', 'Garden Room', 
  'Refurb', 'New Build', 'Insurance', 'Other'
];

const STATUS_COLORS = {
  'Enquiry': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  'Live': 'bg-green-500/20 text-green-400 border-green-500/30',
  'Complete': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Archive': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const TYPE_COLORS = {
  'DEV': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'CON': 'bg-amber-500/20 text-amber-400 border-amber-500/30'
};

// NOTE_CATEGORIES, NOTE_CATEGORY_COLORS, and VARIATION_STATUS_COLORS are imported from components/project-detail

export function ProjectDetail() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [notes, setNotes] = useState([]);
  const [finance, setFinance] = useState(null);
  const [variations, setVariations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [labourSummary, setLabourSummary] = useState(null);
  const [linkedTender, setLinkedTender] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Section visibility toggles - all ON by default
  
  // Section collapse states
  const [invoicesCollapsed, setInvoicesCollapsed] = useState(false);
  const [surveysCollapsed, setSurveysCollapsed] = useState(false);
  const [labourCollapsed, setLabourCollapsed] = useState(true);
  // Tabbed layout — tab lives in the URL so links can deep-link (?tab=costs).
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';
  const setTab = (t) => setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true });
  const [liveRefresh, setLiveRefresh] = useState(0);
  const [documentsCollapsed, setDocumentsCollapsed] = useState(false);
  // variationsCollapsed moved to VariationsSection component
  
  // Labour summary expanded rows
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [employeeDates, setEmployeeDates] = useState({});
  const [loadingDates, setLoadingDates] = useState(null);
  
  // Dialog states
  const [financeDialogOpen, setFinanceDialogOpen] = useState(false);
  // variationDialogOpen moved to VariationsSection component
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [syncingFinance, setSyncingFinance] = useState(false);
  const [showSyncConfirmDialog, setShowSyncConfirmDialog] = useState(false);
  const [syncConfirmData, setSyncConfirmData] = useState(null);
  
  // Invoice form state
  const makeInvoiceLine = (partial = {}) => ({
    _k: `line_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    description: '',
    quantity: 1,
    unit_price: 0,
    vat_rate: 20,
    reverse_charge: false,
    ...partial,
  });
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    line_items: [makeInvoiceLine()],
    notes: '',
    payment_terms: 'Payment due within 14 days of invoice date',
    purchase_order_number: ''
  });
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  
  // Image viewer state
  const [viewerImage, setViewerImage] = useState(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    project_type: '',
    project_source: '',
    primary_project_type: '',
    client_name: '',
    address_line1: '',
    town_city: '',
    postcode: '',
    telephone: '',
    email: '',
    notes: '',
    status: '',
    // Site Diary — colour picked from 12-swatch palette; assigned users list
    // determines who can log a diary entry against this project. Admins may
    // override the auto-assigned colour and add/remove assignees at any time.
    colour: null,
    assigned_user_ids: [],
  });
  // Users list is loaded lazily when the edit dialog opens so we can render
  // an assignee picker without slowing down initial ProjectDetail render.
  const [allUsers, setAllUsers] = useState([]);
  
  // Form states (noteForm moved to NotesSection component)
  const [financeForm, setFinanceForm] = useState({
    contract_value: 0,
    labour: 0,
    materials: 0,
    plant: 0,
    subcontractors: 0,
    prelims: 0,
    prosums: 0,
    overhead_profit: 0,
    costs_incurred: 0
  });
  // variationForm moved to VariationsSection component
  
  const { api, isAdmin, user, canEditRecords, canDeleteRecords } = useAuth();
  const navigate = useNavigate();
  const canEdit = canEditRecords; // Only admins can edit projects
  // Live actuals (diary labour + ledger material/plant) for Overview + Costs tabs
  const liveCosts = useProjectLiveCosts(api, projectId, liveRefresh);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        const [projectRes, surveysRes, notesRes, financeRes, variationsRes, invoicesRes, labourRes] = await Promise.all([
          api.get(`/projects/${projectId}`),
          api.get(`/projects/${projectId}/surveys`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/notes`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/finance`).catch(() => ({ data: null })),
          api.get(`/projects/${projectId}/variations`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/invoices`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/labour-summary`).catch(() => ({ data: null }))
        ]);
        
        if (!isMounted) return; // Prevent state updates on unmounted component
        
        setProject(projectRes.data);
        setSurveys(surveysRes.data);
        setNotes(notesRes.data || []);
        setFinance(financeRes.data);
        setVariations(variationsRes.data || []);
        setInvoices(invoicesRes.data || []);
        setLabourSummary(labourRes.data);
        
        // Fetch invoice settings
        try {
          const settingsRes = await api.get('/invoice-settings');
          if (isMounted) setInvoiceSettings(settingsRes.data);
        } catch (e) {
          console.log('Invoice settings not available');
        }
        
        if (financeRes.data && isMounted) {
          setFinanceForm({
            contract_value: financeRes.data.contract_value || 0,
            labour: financeRes.data.breakdown?.labour || 0,
            materials: financeRes.data.breakdown?.materials || 0,
            plant: financeRes.data.breakdown?.plant || 0,
            subcontractors: financeRes.data.breakdown?.subcontractors || 0,
            prelims: financeRes.data.breakdown?.prelims || 0,
            prosums: financeRes.data.breakdown?.prosums || 0,
            overhead_profit: financeRes.data.breakdown?.overhead_profit || 0,
            costs_incurred: financeRes.data.costs_incurred || 0
        });
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to fetch project:', error);
          toast.error('Failed to load project');
          navigate('/projects');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    loadData();
    
    return () => { isMounted = false; };
  }, [projectId, api, navigate]);

  // Keep fetchProjectData for manual refresh calls
  const fetchProjectData = async () => {
    try {
      const [projectRes, surveysRes, notesRes, financeRes, variationsRes, invoicesRes, labourRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/surveys`).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/notes`).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/finance`).catch(() => ({ data: null })),
        api.get(`/projects/${projectId}/variations`).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/invoices`).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/labour-summary`).catch(() => ({ data: null }))
      ]);
      setProject(projectRes.data);
      setSurveys(surveysRes.data);
      setNotes(notesRes.data || []);
      setFinance(financeRes.data);
      setVariations(variationsRes.data || []);
      setInvoices(invoicesRes.data || []);
      setLabourSummary(labourRes.data);
      
      // Fetch linked tender if project has source_tender_id
      if (projectRes.data?.source_tender_id) {
        try {
          const tenderRes = await api.get(`/tenders/${projectRes.data.source_tender_id}`);
          setLinkedTender(tenderRes.data);
        } catch (e) {
          console.log('No linked tender found');
          setLinkedTender(null);
        }
      } else {
        setLinkedTender(null);
      }
    } catch (error) {
      console.error('Failed to refresh project:', error);
      toast.error('Failed to refresh project');
    }
  };

  // Labour summary - toggle employee dates
  const toggleEmployeeDates = async (employeeId) => {
    if (expandedEmployee === employeeId) {
      setExpandedEmployee(null);
      return;
    }
    
    setExpandedEmployee(employeeId);
    
    // Check if we already have the dates cached
    if (employeeDates[employeeId]) {
      return;
    }
    
    // Fetch dates for this employee
    setLoadingDates(employeeId);
    try {
      const response = await api.get(`/projects/${projectId}/labour-summary/employee/${employeeId}/dates`);
      setEmployeeDates(prev => ({
        ...prev,
        [employeeId]: response.data.dates_worked
      }));
    } catch (error) {
      console.error('Failed to fetch employee dates:', error);
      toast.error('Failed to load work dates');
    } finally {
      setLoadingDates(null);
    }
  };

  // Note handlers moved to NotesSection component

  // Edit Project handlers
  const openEditDialog = () => {
    if (project) {
      // Handle notes field - it could be a string (legacy) or we just ignore it since notes are managed separately
      // The editForm.notes field is for a simple text note, not the array of structured notes
      const notesValue = typeof project.notes === 'string' ? project.notes : '';
      
      setEditForm({
        project_type: project.project_type || 'DEV',
        project_source: project.project_source || 'Main Contractor',
        primary_project_type: project.primary_project_type || '',
        client_name: project.client_name || '',
        address_line1: project.address_line1 || '',
        town_city: project.town_city || '',
        postcode: project.postcode || '',
        telephone: project.telephone || '',
        email: project.email || '',
        notes: notesValue,
        scope: project.scope || '',
        site_contact_name: project.site_contact_name || '',
        site_contact_phone: project.site_contact_phone || '',
        access_notes: project.access_notes || '',
        status: project.status || 'Live',
        colour: project.colour || null,
        assigned_user_ids: project.assigned_user_ids || [],
      });
      // Lazy-load the diary-eligible user list. `/users` (no filter) returns
      // admin + surveyor + office users — the ones eligible to log a diary.
      if (allUsers.length === 0) {
        api.get('/users').then(res => setAllUsers(res.data || [])).catch(() => {});
      }
      setShowEditDialog(true);
    }
  };

  const handleSaveProject = async () => {
    setSubmitting(true);
    try {
      await api.put(`/projects/${projectId}`, editForm);
      toast.success('Project updated');
      setShowEditDialog(false);
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProject = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success('Project deleted successfully');
      navigate('/projects');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    } finally {
      setSubmitting(false);
      setShowDeleteDialog(false);
    }
  };

  // handleDeleteNote moved to NotesSection component

  // Finance handlers
  const handleUpdateFinance = async () => {
    setSubmitting(true);
    try {
      await api.put(`/projects/${projectId}/finance`, {
        contract_value: parseFloat(financeForm.contract_value) || 0,
        breakdown: {
          labour: parseFloat(financeForm.labour) || 0,
          materials: parseFloat(financeForm.materials) || 0,
          plant: parseFloat(financeForm.plant) || 0,
          subcontractors: parseFloat(financeForm.subcontractors) || 0,
          prelims: parseFloat(financeForm.prelims) || 0,
          prosums: parseFloat(financeForm.prosums) || 0,
          overhead_profit: parseFloat(financeForm.overhead_profit) || 0
        }
      });
      toast.success('Finance updated');
      setFinanceDialogOpen(false);
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update finance');
    } finally {
      setSubmitting(false);
    }
  };

  // Variation handlers moved to VariationsSection component

  const handleDeleteSurvey = async (surveyId) => {
    if (!confirm('Delete this site survey?')) return;
    try {
      await api.delete(`/admin/surveys/${surveyId}`);
      toast.success('Survey deleted');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete survey');
    }
  };

  // Invoice handlers
  const fetchInvoiceSettings = async () => {
    try {
      const res = await api.get('/invoice-settings');
      setInvoiceSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch invoice settings');
    }
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({
      invoice_date: format(new Date(), 'yyyy-MM-dd'),
      due_date: format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      line_items: [makeInvoiceLine()],
      notes: '',
      payment_terms: invoiceSettings?.default_payment_terms_text || 'Payment due within 14 days of invoice date',
      purchase_order_number: ''
    });
  };

  const handleAddInvoiceLineItem = () => {
    setInvoiceForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, makeInvoiceLine()]
    }));
  };

  const handleRemoveInvoiceLineItem = (index) => {
    if (invoiceForm.line_items.length <= 1) return;
    setInvoiceForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const handleInvoiceLineItemChange = (index, field, value) => {
    setInvoiceForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateInvoiceTotals = () => {
    let subtotal = 0;
    let vatAmount = 0;
    invoiceForm.line_items.forEach(item => {
      const amount = (item.quantity || 0) * (item.unit_price || 0);
      subtotal += amount;
      vatAmount += amount * ((item.vat_rate || 0) / 100);
    });
    return { subtotal, vatAmount, total: subtotal + vatAmount };
  };

  const handleCreateInvoice = async () => {
    // Filter out empty line items
    const validItems = invoiceForm.line_items.filter(item => item.description.trim());
    
    // Validate - need at least one line item with description
    if (validItems.length === 0) {
      toast.error('Please add at least one line item with a description');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/invoices', {
        project_id: projectId,
        invoice_date: invoiceForm.invoice_date,
        due_date: invoiceForm.due_date,
        line_items: validItems,
        notes: invoiceForm.notes,
        payment_terms: invoiceForm.payment_terms,
        purchase_order_number: invoiceForm.purchase_order_number?.trim() || null
      });
      toast.success('Invoice created successfully');
      setInvoiceDialogOpen(false);
      resetInvoiceForm();
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}/status?status=${newStatus}`);
      toast.success('Invoice status updated');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to update invoice status');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!confirm('Delete this invoice?')) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  // Create folder structure in OneDrive (manual override button)
  const handleCreateFolderStructure = async () => {
    setSubmitting(true);
    try {
      const response = await api.post(`/projects/${projectId}/create-folder-structure`);
      const data = response.data;
      toast.success(
        `Folder structure created successfully. ${data.folders_created} folders created, ${data.folders_existed} already existed.`
      );
      fetchProjectData(); // Refresh to update folder_structure_created status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder structure');
    } finally {
      setSubmitting(false);
    }
  };

  // Sync finance from linked tender
  const handleSyncFinanceFromTender = async (forceOverwrite = false) => {
    setSyncingFinance(true);
    try {
      const response = await api.post(`/projects/${projectId}/sync-finance-from-tender?force_overwrite=${forceOverwrite}`);
      const data = response.data;
      
      if (data.requires_confirmation) {
        // Show confirmation dialog
        setSyncConfirmData(data);
        setShowSyncConfirmDialog(true);
      } else if (data.success) {
        toast.success(data.message || 'Finance synced from tender');
        fetchProjectData(); // Refresh project data
        setShowSyncConfirmDialog(false);
        setSyncConfirmData(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync finance from tender');
    } finally {
      setSyncingFinance(false);
    }
  };

  const confirmSyncFinance = async () => {
    await handleSyncFinanceFromTender(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="container mx-auto px-4 py-6 md:py-8 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/projects')}
          data-testid="back-to-projects-btn"
          className="text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Projects
        </Button>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/diary/${projectId}`)}
            data-testid="open-site-diary-btn"
            className="border-white/20 text-gray-300 hover:text-white hover:bg-white/5"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Site Diary
          </Button>
          {/* Edit Project Button - Admin Only */}
          {canEditRecords && (
            <Button
              variant="outline"
              size="sm"
              onClick={openEditDialog}
              data-testid="edit-project-btn"
              className="border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623]/10"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Project
            </Button>
          )}
          
          {/* Delete Project Button - Admin Only */}
          {canDeleteRecords && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              data-testid="delete-project-btn"
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <ProjectHeader project={project} typeColors={TYPE_COLORS} statusColors={STATUS_COLORS} />

      <ProjectTabs
        tab={tab}
        onChange={setTab}
        counts={{ finance: invoices.length, site: surveys.length, notes: notes.length }}
      />

      <div className="space-y-6">
        {tab === 'overview' && (
          <OverviewTab
            project={project}
            finance={finance}
            summary={liveCosts.summary}
            labourSummary={labourSummary}
            invoices={invoices}
            onGoCosts={() => setTab('costs')}
          />
        )}
        
        {/* Finance Section */}
        {tab === 'finance' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Finance
              </h2>
              <div className="flex items-center gap-2">
                {/* Sync from Tender button - show only if project has a linked tender */}
                {canEdit && project?.source_tender_id && (
                  <Button
                    onClick={() => handleSyncFinanceFromTender(false)}
                    disabled={syncingFinance}
                    data-testid="sync-finance-from-tender-btn"
                    variant="outline"
                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:text-purple-300"
                  >
                    {syncingFinance ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Sync from Tender
                  </Button>
                )}
                {canEdit && (
                  <Button
                    onClick={() => setFinanceDialogOpen(true)}
                    data-testid="edit-finance-btn"
                    variant="outline"
                    className="border-white/20 text-gray-300 hover:bg-white/5"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Finance
                  </Button>
                )}
              </div>
            </div>

            {/* Finance Overview Cards — spend + margin are LIVE (diary + ledger + manual other actuals) */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4" data-testid="finance-cards">
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Contract Value</p>
                  <p className="text-xl font-bold text-white" data-testid="finance-contract-value">{formatCurrency(finance?.contract_value)}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Variations</p>
                  <p className={`text-xl font-bold ${(finance?.variations_total || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="finance-variations">
                    {(finance?.variations_total || 0) >= 0 ? '+' : ''}{formatCurrency(finance?.variations_total)}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-[#2A3240] border-white/10 border-l-2 border-l-blue-400">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1.5">Spend to date <span className="px-1.5 py-0 rounded text-[9px] font-mono uppercase tracking-wider bg-blue-500/15 text-blue-400 border border-blue-500/30">Live</span></p>
                  <p className="text-xl font-bold text-white" data-testid="finance-spend-to-date">{formatCurrency(finance?.costs_incurred)}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">diary + ledger{(finance?.spend_manual || 0) > 0 ? ` · ${formatCurrency(finance.spend_manual)} manual` : ''}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Profit Margin</p>
                  <p className={`text-xl font-bold ${(finance?.profit_margin || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="finance-profit-margin">
                    {finance?.profit_margin || 0}%
                  </p>
                  <p className="text-[10px] text-gray-500 mt-0.5" data-testid="finance-profit-to-date">{formatCurrency(finance?.profit_to_date)} to date</p>
                </CardContent>
              </Card>
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Still to invoice</p>
                  <p className="text-xl font-bold text-orange-400" data-testid="finance-still-to-invoice">{formatCurrency(finance?.still_to_invoice)}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">{formatCurrency(finance?.invoices_paid)} paid of {formatCurrency(finance?.invoices_sent)} raised</p>
                </CardContent>
              </Card>
            </div>

            {/* Contract Breakdown */}
            <Card className="bg-[#2A3240] border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white">Contract Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Labour</p>
                    <p className="text-white font-medium">{formatCurrency(finance?.breakdown?.labour)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Materials</p>
                    <p className="text-white font-medium">{formatCurrency(finance?.breakdown?.materials)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Plant</p>
                    <p className="text-white font-medium">{formatCurrency(finance?.breakdown?.plant)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Subcontractors</p>
                    <p className="text-white font-medium">{formatCurrency(finance?.breakdown?.subcontractors)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Prelims</p>
                    <p className="text-white font-medium">{formatCurrency(finance?.breakdown?.prelims)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Pro Sums</p>
                    <p className="text-white font-medium">{formatCurrency(finance?.breakdown?.prosums)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">O&P</p>
                    <p className="text-white font-medium">{formatCurrency(finance?.breakdown?.overhead_profit)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Client POs assigned to this job (income) */}
            <ProjectClientPoCard projectId={projectId} />
          </div>
        )}

        {/* Costs — Actual vs Tendered (live labour/material/plant) + receipts + diary labour */}
        {tab === 'costs' && project && (
          <div className="mb-6">
            <CostBreakdownSection
              projectId={projectId}
              api={api}
              canEdit={canEdit}
              onUpdate={fetchProjectData}
            />
          </div>
        )}
        {tab === 'costs' && project && (
          <LiveCostCards
            projectId={projectId}
            project={project}
            ledger={liveCosts.ledger}
            labourLog={liveCosts.labourLog}
            labourSummary={labourSummary}
            navigate={navigate}
          />
        )}

        {/* Site — Programme, diary shortcut, surveys */}
        {tab === 'site' && project && (
          <div className="mb-6">
            <ProjectProgrammeSection
              project={project}
              api={api}
              canEdit={canEdit}
              onUpdate={fetchProjectData}
            />
          </div>
        )}
        {tab === 'site' && (
          <button
            type="button"
            onClick={() => navigate(`/diary/${projectId}`)}
            className="w-full rounded-lg border border-[#F5A623]/40 bg-[#F5A623]/10 p-5 text-left hover:bg-[#F5A623]/15 transition-colors flex items-center gap-4"
            data-testid="site-open-diary"
          >
            <BookOpen className="w-8 h-8 text-[#F5A623] flex-shrink-0" />
            <div>
              <div className="text-white font-bold text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Open Site Diary</div>
              <div className="text-xs text-gray-400 mt-0.5">Today's labour, deliveries, receipts, notes and weather</div>
            </div>
          </button>
        )}

        {/* Invoices Section */}
        {tab === 'finance' && (
          <div>
            <div 
              className="flex items-center justify-between mb-4 cursor-pointer"
              onClick={() => setInvoicesCollapsed(!invoicesCollapsed)}
            >
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <Receipt className="w-5 h-5 inline-block mr-2 text-[#F5A623]" />
                  Invoices ({invoices.length})
                </h2>
                {invoicesCollapsed ? (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
              {canEdit && !invoicesCollapsed && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    resetInvoiceForm();
                    setInvoiceDialogOpen(true);
                  }}
                  data-testid="create-invoice-btn"
                  className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              )}
            </div>

            {!invoicesCollapsed && (
              <>
                {invoices.length === 0 ? (
                  <Card className="bg-[#2A3240] border-white/10">
                    <CardContent className="py-8 text-center">
                      <Receipt className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                      <p className="text-gray-400">No invoices yet</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {invoices.map((invoice) => {
                      const statusColors = {
                        draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
                        sent: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                        paid: 'bg-green-500/20 text-green-400 border-green-500/30',
                        overdue: 'bg-red-500/20 text-red-400 border-red-500/30',
                        cancelled: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                      };
                      return (
                        <Card key={invoice.id} className="bg-[#2A3240] border-white/10">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <span className="text-[#F5A623] font-bold text-lg">{invoice.invoice_number}</span>
                                  <Badge className={statusColors[invoice.status] || statusColors.draft}>
                                    {invoice.status?.charAt(0).toUpperCase() + invoice.status?.slice(1)}
                                  </Badge>
                                </div>
                                <p className="text-white font-medium">{invoice.client_name}</p>
                                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-400">
                                  {invoice.invoice_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {format(parseISO(invoice.invoice_date), 'dd MMM yyyy')}
                                    </span>
                                  )}
                                  {invoice.due_date && <span>Due: {format(parseISO(invoice.due_date), 'dd MMM yyyy')}</span>}
                                  <span>{invoice.line_items?.length || 0} items</span>
                                  {invoice.purchase_order_number && (
                                    <span className="flex items-center gap-1 text-sky-400">
                                      <FileText className="w-3 h-3" />
                                      PO: {invoice.purchase_order_number}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-white">
                                  £{invoice.total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                </p>
                                <p className="text-xs text-gray-500">
                                  (excl. VAT: £{invoice.subtotal?.toLocaleString('en-GB', { minimumFractionDigits: 2 })})
                                </p>
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setViewInvoice(invoice)}
                                  className="border-white/20 text-gray-300 hover:bg-white/10"
                                  data-testid={`view-invoice-${invoice.id}`}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  View
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled
                                  className="border-white/20 text-gray-500 cursor-not-allowed"
                                  title="Xero integration coming soon"
                                  data-testid={`send-xero-${invoice.id}`}
                                >
                                  <Send className="w-3 h-3 mr-1" />
                                  Send to Xero
                                </Button>
                              </div>
                              <div className="flex items-center gap-2">
                                {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                                  <Select
                                    value={invoice.status}
                                    onValueChange={(value) => handleUpdateInvoiceStatus(invoice.id, value)}
                                  >
                                    <SelectTrigger className="w-[120px] h-8 bg-[#1E2530] border-white/20 text-white text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#2A3240] border-white/10">
                                      <SelectItem value="draft" className="text-white">Draft</SelectItem>
                                      <SelectItem value="sent" className="text-white">Sent</SelectItem>
                                      <SelectItem value="paid" className="text-white">Paid</SelectItem>
                                      <SelectItem value="overdue" className="text-white">Overdue</SelectItem>
                                      <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
                                    </SelectContent>
                                  </Select>
                                )}
                                {canEdit && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteInvoice(invoice.id)}
                                    className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                    data-testid={`delete-invoice-${invoice.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Labour Summary Section (schedule-based plan detail, collapsed by default) */}
        {tab === 'costs' && (
          <div>
            <div 
              className="flex items-center justify-between mb-4 cursor-pointer"
              onClick={() => setLabourCollapsed(!labourCollapsed)}
            >
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <HardHat className="w-5 h-5 inline-block mr-2 text-[#F5A623]" />
                  Scheduled labour · plan detail
                </h2>
                {labourCollapsed ? (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>

            {!labourCollapsed && (
              <>
            {/* Labour Overview Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Total Days Worked</p>
                  <p className="text-2xl font-bold text-white">{labourSummary?.total_days || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Total Labour Cost</p>
                  <p className="text-2xl font-bold text-[#F5A623]">{formatCurrency(labourSummary?.total_cost)}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Workers Assigned</p>
                  <p className="text-2xl font-bold text-white">{labourSummary?.by_employee?.length || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Labour by Trade */}
            {labourSummary?.by_trade?.length > 0 && (
              <Card className="bg-[#2A3240] border-white/10 mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-white">By Trade</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {labourSummary.by_trade.map((trade, idx) => (
                      <div key={trade.trade || idx} className="flex items-center justify-between p-3 bg-[#1E2530] rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
                            <HardHat className="w-5 h-5 text-[#F5A623]" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{trade.trade}</p>
                            <p className="text-xs text-gray-500">{trade.employees.join(', ')}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-bold">{trade.days_worked} day{trade.days_worked !== 1 ? 's' : ''}</p>
                          <p className="text-sm text-[#F5A623]">{formatCurrency(trade.total_cost)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Labour by Employee */}
            {labourSummary?.by_employee?.length > 0 ? (
              <Card className="bg-[#2A3240] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-white">By Employee</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left text-xs text-gray-500 pb-2 font-medium">Employee</th>
                          <th className="text-left text-xs text-gray-500 pb-2 font-medium">Trade</th>
                          <th className="text-right text-xs text-gray-500 pb-2 font-medium">Daily Rate</th>
                          <th className="text-right text-xs text-gray-500 pb-2 font-medium">Days</th>
                          <th className="text-right text-xs text-gray-500 pb-2 font-medium">Total Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {labourSummary.by_employee.map((emp, idx) => (
                          <Fragment key={emp.employee_id || idx}>
                            <tr 
                              className={`border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${expandedEmployee === emp.employee_id ? 'bg-white/5' : ''}`}
                              onClick={() => toggleEmployeeDates(emp.employee_id)}
                            >
                              <td className="py-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
                                    <User className="w-4 h-4 text-[#F5A623]" />
                                  </div>
                                  <span className="text-white font-medium">{emp.employee_name}</span>
                                  {expandedEmployee === emp.employee_id ? (
                                    <ChevronUp className="w-4 h-4 text-gray-400" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                  )}
                                </div>
                              </td>
                              <td className="py-3 text-gray-400">{emp.trade || '-'}</td>
                              <td className="py-3 text-right text-gray-400">
                                {emp.pay_rate > 0 ? formatCurrency(emp.pay_rate) : '-'}
                              </td>
                              <td className="py-3 text-right text-white font-medium">{emp.days_worked}</td>
                              <td className="py-3 text-right text-[#F5A623] font-bold">{formatCurrency(emp.total_cost)}</td>
                            </tr>
                            {expandedEmployee === emp.employee_id && (
                              <tr>
                                <td colSpan="5" className="py-3 px-4 bg-[#1E2530]">
                                  {loadingDates === emp.employee_id ? (
                                    <div className="flex items-center justify-center py-4">
                                      <Loader2 className="w-5 h-5 animate-spin text-[#F5A623]" />
                                      <span className="ml-2 text-gray-400">Loading dates...</span>
                                    </div>
                                  ) : employeeDates[emp.employee_id]?.length > 0 ? (
                                    <div>
                                      <p className="text-sm text-gray-400 mb-2">Dates worked on this project:</p>
                                      <div className="flex flex-wrap gap-2">
                                        {employeeDates[emp.employee_id].map((date) => (
                                          <span 
                                            key={date}
                                            className="px-2 py-1 bg-[#2A3240] rounded text-xs text-white"
                                          >
                                            {format(parseISO(date), 'EEE, dd MMM yyyy')}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-500">No dates found</p>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-white/20">
                          <td colSpan="3" className="py-3 text-white font-bold">Total</td>
                          <td className="py-3 text-right text-white font-bold">{labourSummary.total_days}</td>
                          <td className="py-3 text-right text-[#F5A623] font-bold text-lg">{formatCurrency(labourSummary.total_cost)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="py-8 text-center">
                  <Users className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-400">No labour scheduled for this project yet</p>
                  <p className="text-sm text-gray-500 mt-1">Schedule workers from the Schedule page</p>
                </CardContent>
              </Card>
            )}
              </>
            )}
          </div>
        )}

        {/* Documents Section */}
        {tab === 'documents' && project && (
          <div className="mb-6">
            <div 
              className="flex items-center justify-between mb-4"
            >
              <div 
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => setDocumentsCollapsed(!documentsCollapsed)}
              >
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <FolderOpen className="w-5 h-5 inline-block mr-2 text-[#F5A623]" />
                  Documents
                </h2>
                {/* Folder Status Indicator */}
                {project.folder_structure_created ? (
                  <span className="flex items-center gap-1 ml-2" title="Folder structure created">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-xs text-green-500">Structure Ready</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1 ml-2" title={project.folders_skipped_by ? `Folder creation was skipped by ${project.folders_skipped_by} when the tender was won` : 'Folder structure not yet created'} data-testid="project-folders-status">
                    <span className={`w-3 h-3 rounded-full ${project.folders_skipped_at ? 'bg-amber-400' : 'bg-gray-500'}`}></span>
                    <span className={`text-xs ${project.folders_skipped_at ? 'text-amber-300' : 'text-gray-400'}`}>{project.folders_skipped_at ? 'Folders not created yet' : 'No Structure'}</span>
                  </span>
                )}
                {documentsCollapsed ? (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
              {/* Create Folder Structure Button - Always visible for admins */}
              {isAdmin && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623]/10"
                      data-testid="create-folder-structure-btn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FolderOpen className="w-4 h-4 mr-1" />
                      {project.folder_structure_created ? 'Verify/Repair Folders' : 'Create Folder Structure'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#2A3240] border-white/10">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">
                        {project.folder_structure_created ? 'Verify Folder Structure' : 'Create Folder Structure'}
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-gray-400">
                        This will create any missing folders in your OneDrive structure. 
                        Existing folders and their contents will not be affected.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="border-white/20 text-gray-300">Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleCreateFolderStructure}
                        className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
                      >
                        Continue
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
            {!documentsCollapsed && (
              <FileBrowser
                entityType="project"
                entityId={project.id}
                entityRef={project.project_ref}
                canUpload={canEdit}
                canDelete={isAdmin}
              />
            )}
          </div>
        )}

        {/* Variations Section */}
        {tab === 'finance' && (
          <VariationsSection
            variations={variations}
            projectId={projectId}
            api={api}
            onVariationsChange={setVariations}
            canEdit={canEdit}
            canDelete={canDeleteRecords}
          />
        )}

        {/* Site Surveys Section */}
        {tab === 'site' && (
          <div>
            <div 
              className="flex items-center justify-between mb-4 cursor-pointer"
              onClick={() => setSurveysCollapsed(!surveysCollapsed)}
            >
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <ClipboardList className="w-5 h-5 inline-block mr-2 text-[#F5A623]" />
                  Site Surveys ({surveys.length})
                </h2>
                {surveysCollapsed ? (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
              {!surveysCollapsed && (
                <span className="text-sm text-gray-500 italic">
                  Surveys are created via the mobile portal
                </span>
              )}
            </div>

            {!surveysCollapsed && (
              <>
                {surveys.length === 0 ? (
                  <Card className="bg-[#2A3240] border-white/10">
                    <CardContent className="py-8 text-center">
                      <ClipboardList className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                      <p className="text-gray-400">No site surveys yet</p>
                      <p className="text-gray-500 text-sm mt-1">Site surveys are created by operatives via the mobile portal</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {surveys.map((survey) => (
                      <Card key={survey.id} className="bg-[#2A3240] border-white/10 h-full">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <FileText className="w-5 h-5 text-[#F5A623] flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs capitalize">
                              {survey.survey_type}
                            </Badge>
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSurvey(survey.id)}
                                className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                data-testid={`delete-survey-${survey.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                          <p className="text-sm text-gray-300 line-clamp-3 mt-1">{survey.notes}</p>
                          <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {survey.created_by_name}
                            </span>
                            <span>{format(parseISO(survey.created_at), 'dd MMM yyyy HH:mm')}</span>
                          </div>
                          {survey.media?.length > 0 && (
                            <div className="mt-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Image className="w-3 h-3 text-gray-500" />
                                <span className="text-xs text-gray-500">{survey.media.length} file(s)</span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {survey.media.map((m) => {
                                  const isImage = m.file_type === 'image' || m.file_type?.startsWith('image');
                                  const isVideo = m.file_type === 'video' || m.file_type?.startsWith('video');
                                  
                                  return (
                                  <div key={m.id} className="relative group">
                                    {isImage && m.data_url ? (
                                      <img
                                        src={m.data_url}
                                        alt={m.filename}
                                        className="w-16 h-16 object-cover rounded border border-white/10 cursor-pointer hover:border-[#F5A623]"
                                        onClick={() => setViewerImage({ url: m.data_url, filename: m.filename })}
                                        data-testid={`survey-image-${m.id}`}
                                      />
                                    ) : isVideo ? (
                                      <div 
                                        className="w-16 h-16 bg-[#1E2530] rounded border border-white/10 flex flex-col items-center justify-center cursor-pointer hover:border-[#F5A623] relative"
                                        onClick={() => m.data_url && window.open(m.data_url, '_blank')}
                                        data-testid={`survey-video-${m.id}`}
                                      >
                                        <Film className="w-6 h-6 text-purple-400" />
                                        <Play className="w-3 h-3 text-purple-400 mt-1" />
                                      </div>
                                    ) : (
                                      <div 
                                        className="w-16 h-16 bg-[#1E2530] rounded border border-white/10 flex items-center justify-center cursor-pointer hover:border-[#F5A623]"
                                        onClick={() => m.data_url && setViewerImage({ url: m.data_url, filename: m.filename })}
                                      >
                                        <FileText className="w-6 h-6 text-gray-500" />
                                      </div>
                                    )}
                                    <span className="absolute -bottom-5 left-0 text-[10px] text-gray-500 truncate max-w-[64px]">
                                      {m.filename}
                                    </span>
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
        </div>
      )}

        {/* Notes Section */}
        {tab === 'notes' && (
          <NotesSection
            notes={notes}
            projectId={projectId}
            api={api}
            onNotesChange={setNotes}
          />
        )}
      </div>

      {/* Edit Finance Dialog */}
      <Dialog open={financeDialogOpen} onOpenChange={setFinanceDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Finance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Contract Value</Label>
              <Input
                type="number"
                value={financeForm.contract_value}
                onChange={(e) => setFinanceForm(prev => ({ ...prev, contract_value: e.target.value }))}
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Labour</Label>
                <Input
                  type="number"
                  value={financeForm.labour}
                  onChange={(e) => setFinanceForm(prev => ({ ...prev, labour: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Materials</Label>
                <Input
                  type="number"
                  value={financeForm.materials}
                  onChange={(e) => setFinanceForm(prev => ({ ...prev, materials: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Plant</Label>
                <Input
                  type="number"
                  value={financeForm.plant}
                  onChange={(e) => setFinanceForm(prev => ({ ...prev, plant: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Subcontractors</Label>
                <Input
                  type="number"
                  value={financeForm.subcontractors}
                  onChange={(e) => setFinanceForm(prev => ({ ...prev, subcontractors: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Prelims</Label>
                <Input
                  type="number"
                  value={financeForm.prelims}
                  onChange={(e) => setFinanceForm(prev => ({ ...prev, prelims: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Pro Sums</Label>
                <Input
                  type="number"
                  value={financeForm.prosums}
                  onChange={(e) => setFinanceForm(prev => ({ ...prev, prosums: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">O&P</Label>
                <Input
                  type="number"
                  value={financeForm.overhead_profit}
                  onChange={(e) => setFinanceForm(prev => ({ ...prev, overhead_profit: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
            </div>
            <div className="rounded-md bg-[#1E2530] border border-white/10 px-3 py-2 text-xs text-gray-400" data-testid="finance-costs-live-note">
              Spend to date is live from the Site Diary and Cost Ledger (plus any manual actuals on the Costs tab) — there's nothing to type here.
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setFinanceDialogOpen(false)} className="flex-1 border-white/20 text-gray-300">
                Cancel
              </Button>
              <Button onClick={handleUpdateFinance} disabled={submitting} className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Finance'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Variation Dialog moved to VariationsSection component */}

      {/* Edit Project Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#F5A623]" />
              Edit Project
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Status - Most Important */}
            <div className="space-y-2 p-3 bg-[#1E2530] rounded-lg border border-white/10">
              <Label className="text-gray-300 font-medium">Project Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="bg-[#2A3240] border-white/20 text-white" data-testid="edit-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {['Enquiry', 'Live', 'Complete', 'Archive'].map(s => (
                    <SelectItem key={s} value={s} className="text-white focus:bg-[#F5A623]/20 focus:text-white">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Project Type & Source */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Project Type</Label>
                <Select value={editForm.project_type} onValueChange={(v) => setEditForm(prev => ({ ...prev, project_type: v }))}>
                  <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    <SelectItem value="DEV" className="text-white focus:bg-[#F5A623]/20 focus:text-white">DEV - Development</SelectItem>
                    <SelectItem value="CON" className="text-white focus:bg-[#F5A623]/20 focus:text-white">CON - Construction</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Project Source</Label>
                <Select value={editForm.project_source} onValueChange={(v) => setEditForm(prev => ({ ...prev, project_source: v }))}>
                  <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    <SelectItem value="Main Contractor" className="text-white focus:bg-[#F5A623]/20 focus:text-white">Main Contractor</SelectItem>
                    <SelectItem value="Residential Client" className="text-white focus:bg-[#F5A623]/20 focus:text-white">Residential Client</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Primary Project Type (Extension, Loft Conversion, etc.) */}
            <div className="space-y-2">
              <Label className="text-gray-300">Project Category</Label>
              <Select 
                value={editForm.primary_project_type || ''} 
                onValueChange={(v) => setEditForm(prev => ({ ...prev, primary_project_type: v }))}
              >
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="edit-primary-project-type">
                  <SelectValue placeholder="Select project category..." />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {PROJECT_TYPE_OPTIONS.map(type => (
                    <SelectItem 
                      key={type} 
                      value={type} 
                      className="text-white focus:bg-[#F5A623]/20 focus:text-white"
                    >
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Extension, Loft Conversion, Refurb, etc.</p>
            </div>

            {/* Client Name */}
            <div className="space-y-2">
              <Label className="text-gray-300">Client Name</Label>
              <Input
                value={editForm.client_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, client_name: e.target.value }))}
                placeholder="Client name"
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Telephone</Label>
                <Input
                  value={editForm.telephone}
                  onChange={(e) => setEditForm(prev => ({ ...prev, telephone: e.target.value }))}
                  placeholder="Phone number"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Email</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Email address"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label className="text-gray-300">Address Line 1</Label>
              <Input
                value={editForm.address_line1}
                onChange={(e) => setEditForm(prev => ({ ...prev, address_line1: e.target.value }))}
                placeholder="Street address"
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Town/City</Label>
                <Input
                  value={editForm.town_city}
                  onChange={(e) => setEditForm(prev => ({ ...prev, town_city: e.target.value }))}
                  placeholder="Town or city"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Postcode</Label>
                <Input
                  value={editForm.postcode}
                  onChange={(e) => setEditForm(prev => ({ ...prev, postcode: e.target.value.toUpperCase() }))}
                  placeholder="Postcode"
                  className="bg-[#1E2530] border-white/20 text-white uppercase"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-gray-300">Project Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Internal notes about this project..."
                rows={3}
                className="bg-[#1E2530] border-white/20 text-white resize-none"
              />
            </div>

            {/* Job Detail (field portal) — what operatives see on site */}
            <div className="pt-4 border-t border-white/10 space-y-3" data-testid="edit-job-detail-section">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Job detail (shown to the field)</div>
              <div className="space-y-2">
                <Label className="text-gray-300">Scope / description</Label>
                <Textarea
                  value={editForm.scope || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, scope: e.target.value }))}
                  placeholder="What's being done on this job"
                  rows={3}
                  className="bg-[#1E2530] border-white/20 text-white resize-none"
                  data-testid="edit-scope"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-300">Site contact name</Label>
                  <Input
                    value={editForm.site_contact_name || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, site_contact_name: e.target.value }))}
                    placeholder="Name"
                    className="bg-[#1E2530] border-white/20 text-white"
                    data-testid="edit-site-contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Site contact phone</Label>
                  <Input
                    value={editForm.site_contact_phone || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, site_contact_phone: e.target.value }))}
                    placeholder="07…"
                    inputMode="tel"
                    className="bg-[#1E2530] border-white/20 text-white"
                    data-testid="edit-site-contact-phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Access / parking notes</Label>
                <Textarea
                  value={editForm.access_notes || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, access_notes: e.target.value }))}
                  placeholder="Gate codes, where to park, key safe…"
                  rows={2}
                  className="bg-[#1E2530] border-white/20 text-white resize-none"
                  data-testid="edit-access-notes"
                />
              </div>
            </div>

            {/* Site Diary — colour + assigned users (admins only) */}
            <div className="pt-4 border-t border-white/10 space-y-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Site Diary</div>

              <div>
                <Label className="text-gray-300 font-medium text-sm">Project colour</Label>
                <p className="text-[11px] text-gray-500 mb-2">Shown on the diary picker card and the coloured banner while logging.</p>
                <div className="flex flex-wrap gap-2">
                  {DIARY_COLOUR_KEYS.map(key => {
                    const swatch = DIARY_COLOURS[key];
                    const active = editForm.colour === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setEditForm(prev => ({ ...prev, colour: key }))}
                        className={`w-7 h-7 rounded-full border-2 transition-all ${active ? 'border-white scale-110' : 'border-white/20 hover:border-white/50'}`}
                        style={{ backgroundColor: swatch.hex }}
                        title={key}
                        data-testid={`project-colour-${key}`}
                      />
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-gray-300 font-medium text-sm">Assigned diary users</Label>
                <p className="text-[11px] text-gray-500 mb-2">Only users listed here can log a Site Diary entry against this project. Admins can always see all projects.</p>
                <div className="rounded-md border border-white/10 bg-[#1E2530] max-h-40 overflow-y-auto">
                  {allUsers.length === 0 ? (
                    <div className="text-xs text-gray-500 p-3">Loading users…</div>
                  ) : (
                    allUsers.map(u => {
                      const active = (editForm.assigned_user_ids || []).includes(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            const current = editForm.assigned_user_ids || [];
                            const next = active ? current.filter(id => id !== u.id) : [...current, u.id];
                            setEditForm(prev => ({ ...prev, assigned_user_ids: next }));
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs border-b border-white/5 last:border-b-0 ${active ? 'bg-[#F5A623]/10 text-[#F5A623]' : 'text-white hover:bg-white/5'}`}
                          data-testid={`assign-user-${u.id}`}
                        >
                          <span className={`w-3.5 h-3.5 rounded border ${active ? 'bg-[#F5A623] border-[#F5A623]' : 'border-white/30'} flex items-center justify-center flex-shrink-0`}>
                            {active && <span className="text-[9px] text-[#1E2530] font-bold">✓</span>}
                          </span>
                          <span className="flex-1 truncate">
                            {u.name}
                            <span className="text-gray-500 ml-1">· {u.role || u.user_type}</span>
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowEditDialog(false)} 
                className="flex-1 border-white/20 text-gray-300 hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveProject} 
                disabled={submitting} 
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
                data-testid="save-project-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Project Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Delete Project
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-gray-300">
              Are you sure you want to delete <strong className="text-white">{project?.project_ref}</strong>?
            </p>
            <p className="text-sm text-gray-400">
              This project will be moved to trash and can be restored within 30 days. 
              <span className="text-amber-400"> OneDrive files will be preserved.</span>
            </p>
            <p className="text-sm text-amber-400">
              After 30 days, the project and all its data will be permanently deleted.
            </p>
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteDialog(false)} 
                className="flex-1 border-white/20 text-gray-300 hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteProject} 
                disabled={submitting} 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-project-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Move to Trash'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Viewer Modal */}
      {viewerImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setViewerImage(null)}
          data-testid="image-viewer-modal"
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setViewerImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-[#F5A623] p-2"
              data-testid="close-image-viewer"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={viewerImage.url}
              alt={viewerImage.filename}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-center text-white mt-2 text-sm">{viewerImage.filename}</p>
          </div>
        </div>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#F5A623]" />
              Create Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Client Info (auto-populated) */}
            <div className="bg-[#1E2530] rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Client</p>
              <p className="text-white font-medium">{project?.client_name}</p>
              <p className="text-sm text-gray-400">{project?.address_line1}, {project?.town_city}, {project?.postcode}</p>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Invoice Date</Label>
                <Input
                  type="date"
                  value={invoiceForm.invoice_date}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, invoice_date: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Due Date</Label>
                <Input
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, due_date: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
            </div>

            {/* Purchase Order Number */}
            <div className="space-y-2">
              <Label className="text-gray-300">Purchase Order Number (Optional)</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Enter PO number if applicable"
                  value={invoiceForm.purchase_order_number}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, purchase_order_number: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white flex-1"
                />
                {invoiceForm.purchase_order_number?.trim() && (
                  <Button
                    type="button"
                    onClick={() => {
                      const poDescription = `Invoice in relation to Purchase Order ${invoiceForm.purchase_order_number.trim()}`;
                      // Add as first line item, replacing the empty first item if it exists
                      setInvoiceForm(prev => {
                        const existingItems = prev.line_items.filter(item => item.description.trim());
                        return {
                          ...prev,
                          line_items: [
                            { description: poDescription, quantity: 1, unit_price: 0, vat_rate: 20, reverse_charge: false },
                            ...existingItems
                          ]
                        };
                      });
                    }}
                    className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] px-3"
                    title="Add PO as line item"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {invoiceForm.purchase_order_number?.trim() && (
                <p className="text-xs text-gray-500">
                  Click + to add as line item: "Invoice in relation to Purchase Order {invoiceForm.purchase_order_number.trim()}"
                </p>
              )}
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-gray-300">Line Items</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddInvoiceLineItem}
                  className="border-white/20 text-gray-300 hover:bg-white/10"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {invoiceForm.line_items.map((item, index) => (
                  <div key={item._k || index} className="bg-[#1E2530] rounded-lg p-3 space-y-3">
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <Input
                          placeholder="Description (e.g., Stage Payment: Completion of masonry)"
                          value={item.description}
                          onChange={(e) => handleInvoiceLineItemChange(index, 'description', e.target.value)}
                          className="bg-[#2A3240] border-white/20 text-white"
                        />
                      </div>
                      {invoiceForm.line_items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveInvoiceLineItem(index)}
                          className="h-10 w-10 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Qty</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.quantity}
                          onChange={(e) => handleInvoiceLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="bg-[#2A3240] border-white/20 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Unit Price (£)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unit_price}
                          onChange={(e) => handleInvoiceLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="bg-[#2A3240] border-white/20 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">VAT %</Label>
                        <Select
                          value={item.vat_rate === 0 && item.reverse_charge ? 'reverse' : String(item.vat_rate)}
                          onValueChange={(v) => {
                            if (v === 'reverse') {
                              handleInvoiceLineItemChange(index, 'vat_rate', 0);
                              handleInvoiceLineItemChange(index, 'reverse_charge', true);
                            } else {
                              handleInvoiceLineItemChange(index, 'vat_rate', parseFloat(v));
                              handleInvoiceLineItemChange(index, 'reverse_charge', false);
                            }
                          }}
                        >
                          <SelectTrigger className="bg-[#2A3240] border-white/20 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#2A3240] border-white/10">
                            <SelectItem value="0" className="text-white">0%</SelectItem>
                            <SelectItem value="5" className="text-white">5%</SelectItem>
                            <SelectItem value="20" className="text-white">20%</SelectItem>
                            <SelectItem value="reverse" className="text-white">0% Domestic Reverse Charge</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Amount</Label>
                        <div className="h-10 flex items-center px-3 bg-[#2A3240] border border-white/20 rounded-md text-white">
                          £{((item.quantity || 0) * (item.unit_price || 0)).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-[#1E2530] rounded-lg p-4 space-y-2">
              {(() => {
                const { subtotal, vatAmount, total } = calculateInvoiceTotals();
                return (
                  <>
                    <div className="flex justify-between text-gray-400">
                      <span>Subtotal</span>
                      <span>£{subtotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>VAT</span>
                      <span>£{vatAmount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-white/10">
                      <span>Total</span>
                      <span className="text-[#F5A623]">£{total.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-gray-300">Notes (optional)</Label>
              <Textarea
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes for this invoice..."
                className="bg-[#1E2530] border-white/20 text-white min-h-[60px]"
              />
            </div>

            {/* Payment Terms */}
            <div className="space-y-2">
              <Label className="text-gray-300">Payment Terms</Label>
              <Input
                value={invoiceForm.payment_terms}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, payment_terms: e.target.value }))}
                placeholder="Payment due within 14 days of invoice date"
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setInvoiceDialogOpen(false)}
                className="flex-1 border-white/20 text-gray-300 hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateInvoice}
                disabled={submitting}
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
                data-testid="submit-invoice-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Invoice'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Invoice Modal */}
      {viewInvoice && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Invoice Header */}
            <div className="p-8 border-b">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    LUX DEVELOPMENTS
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    {invoiceSettings?.company_address || '1 Chapel Lane, Hackleton, Northampton, Northamptonshire, NN7 2AH'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Company Reg: {invoiceSettings?.company_registration || '11917152'} | VAT: {invoiceSettings?.vat_number || '344359001'}
                  </p>
                </div>
                <div className="text-right">
                  <h2 className="text-3xl font-bold text-gray-900">{viewInvoice.invoice_number}</h2>
                  <Badge className={`mt-2 ${viewInvoice.status === 'paid' ? 'bg-green-100 text-green-800' : viewInvoice.status === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                    {viewInvoice.status?.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="p-8">
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Bill To:</p>
                  <p className="font-semibold text-gray-900">{viewInvoice.client_name}</p>
                  <p className="text-sm text-gray-600">{viewInvoice.client_address}</p>
                </div>
                <div className="text-right">
                  <div className="mb-2">
                    <p className="text-sm text-gray-500">Invoice Date</p>
                    <p className="font-medium text-gray-900">{viewInvoice.invoice_date ? format(parseISO(viewInvoice.invoice_date), 'dd MMMM yyyy') : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Due Date</p>
                    <p className="font-medium text-gray-900">{viewInvoice.due_date ? format(parseISO(viewInvoice.due_date), 'dd MMMM yyyy') : 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Project Reference */}
              <div className="mb-6 p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-500">Project Reference</p>
                <p className="font-medium text-gray-900">{viewInvoice.project_ref}</p>
              </div>

              {/* Purchase Order Number */}
              {viewInvoice.purchase_order_number && (
                <div className="mb-6 p-3 bg-blue-50 rounded border border-blue-100">
                  <p className="text-sm text-blue-600">Purchase Order Number</p>
                  <p className="font-medium text-blue-900">{viewInvoice.purchase_order_number}</p>
                </div>
              )}

              {/* Line Items Table */}
              <table className="w-full mb-6">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 text-sm font-semibold text-gray-700 w-20">Qty</th>
                    <th className="text-right py-3 text-sm font-semibold text-gray-700 w-28">Unit Price</th>
                    <th className="text-right py-3 text-sm font-semibold text-gray-700 w-20">VAT</th>
                    <th className="text-right py-3 text-sm font-semibold text-gray-700 w-28">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {viewInvoice.line_items?.map((item, idx) => (
                    <tr key={item._k || `${item.description || 'line'}-${idx}`} className="border-b border-gray-100">
                      <td className="py-3 text-gray-900">{item.description}</td>
                      <td className="py-3 text-right text-gray-600">{item.quantity}</td>
                      <td className="py-3 text-right text-gray-600">£{item.unit_price?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                      <td className="py-3 text-right text-gray-600">
                        {item.reverse_charge ? '0% DRC' : `${item.vat_rate}%`}
                      </td>
                      <td className="py-3 text-right font-medium text-gray-900">£{item.amount?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>£{viewInvoice.subtotal?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                  {viewInvoice.line_items?.some(item => item.reverse_charge) ? (
                    <div className="flex justify-between text-gray-600">
                      <span>VAT (Domestic Reverse Charge)</span>
                      <span>£0.00</span>
                    </div>
                  ) : (
                    <div className="flex justify-between text-gray-600">
                      <span>VAT</span>
                      <span>£{viewInvoice.vat_amount?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-200 text-gray-900">
                    <span>Total GBP</span>
                    <span>£{viewInvoice.total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">Payment Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Bank/Sort Code</p>
                    <p className="font-medium text-gray-900">{invoiceSettings?.bank_sort_code || '60-83-71'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Account Number</p>
                    <p className="font-medium text-gray-900">{invoiceSettings?.bank_account_number || '37872989'}</p>
                  </div>
                </div>
                {viewInvoice.payment_terms && (
                  <p className="mt-4 text-sm text-gray-600">{viewInvoice.payment_terms}</p>
                )}
              </div>

              {/* Notes */}
              {viewInvoice.notes && (
                <div className="mt-6 p-4 bg-gray-50 rounded">
                  <p className="text-sm font-medium text-gray-500 mb-1">Notes</p>
                  <p className="text-gray-700">{viewInvoice.notes}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 bg-gray-50 border-t flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => window.print()}
                className="border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button
                onClick={() => setViewInvoice(null)}
                className="bg-[#1E2530] text-white hover:bg-[#2A3240]"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Finance Confirmation Dialog */}
      <AlertDialog open={showSyncConfirmDialog} onOpenChange={setShowSyncConfirmDialog}>
        <AlertDialogContent className="bg-[#2A3240] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Overwrite Finance Data?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This project already has finance data with a Contract Value of{' '}
              <span className="text-[#F5A623] font-medium">
                {formatCurrency(syncConfirmData?.existing_contract_value)}
              </span>.
              <br /><br />
              Overwrite with values from tender{' '}
              <span className="text-purple-400 font-medium">{syncConfirmData?.tender_ref}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setShowSyncConfirmDialog(false);
                setSyncConfirmData(null);
              }}
              className="bg-transparent border-white/20 text-gray-300 hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSyncFinance}
              disabled={syncingFinance}
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              {syncingFinance ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Yes, Overwrite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <CaptureFAB projectId={project?.id} company={project?.project_type === 'CON' ? 'CON' : 'DEV'} />
    </div>
  );
}
